import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// shadcn UI components
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../components/ui/use-toast';
import { Loader } from '../../components/ui/loader';

// icons
import { 
  Add, 
  Edit, 
  DollarCircle, 
  Profile2User, 
  TickCircle, 
  CloseCircle, 
  DocumentText,
  Activity,
  Refresh2
} from 'iconsax-react';

// project imports
import { getPlans, getOrganizations } from '../../services/subscriptionService';

// ==============================|| SUBSCRIPTION PLANS CATALOG ||============================== //

const PlanList = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [plans, setPlans] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [plansRes, orgsRes] = await Promise.all([
        getPlans(),
        getOrganizations()
      ]);
      setPlans(plansRes.data || []);
      setOrganizations(orgsRes.data || []);
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load subscription plans catalog."
      });
    } finally {
      setLoading(false);
    }
  };

  // Pre-calculate enrollments to avoid O(N*M) lookups
  const planEnrollments = organizations.reduce((acc, org) => {
    if (org.planStatus !== 'cancelled' && org.planCode) {
      acc[org.planCode] = (acc[org.planCode] || 0) + 1;
    }
    return acc;
  }, {});

  // Helper: Count orgs enrolled in a specific plan
  const getEnrollmentCount = (planCode) => {
    return planEnrollments[planCode] || 0;
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Subscription Plans
          </h1>
          <p className="text-sm text-muted-foreground">
            Manage product tiers, pricing, features, and resource quotas across the platform
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchData}
            className="flex items-center gap-1.5"
          >
            <Refresh2 size={16} />
            Refresh
          </Button>
          <Button 
            onClick={() => navigate('/subscriptions/plans/new')}
            className="bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-1.5"
          >
            <Add size={16} />
            Create Plan
          </Button>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-card text-card-foreground shadow-sm">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-purple-50 dark:bg-purple-950/20 text-purple-600 dark:text-purple-400 rounded-xl">
              <DocumentText size={24} />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Total Tiers</p>
              <h3 className="text-2xl font-bold">{loading ? '...' : plans.length}</h3>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card text-card-foreground shadow-sm">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-emerald-50 dark:bg-emerald-950/20 text-emerald-600 dark:text-emerald-400 rounded-xl">
              <Profile2User size={24} />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Subscribed Agencies</p>
              <h3 className="text-2xl font-bold">
                {loading ? '...' : organizations.filter(o => o.planStatus === 'active' || o.planStatus === 'trialing').length}
              </h3>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card text-card-foreground shadow-sm">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-blue-50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 rounded-xl">
              <Activity size={24} />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Active Trials</p>
              <h3 className="text-2xl font-bold">
                {loading ? '...' : organizations.filter(o => o.planStatus === 'trialing').length}
              </h3>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card text-card-foreground shadow-sm">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-amber-50 dark:bg-amber-950/20 text-amber-600 dark:text-amber-400 rounded-xl">
              <DollarCircle size={24} />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Estimated Monthly Billing</p>
              <h3 className="text-2xl font-bold">
                {loading ? '...' : `₹${organizations.reduce((acc, org) => {
                  if (org.planStatus !== 'active' || !org.planId) return acc;
                  return acc + (org.planId.price?.monthly || 0);
                }, 0)}`}
              </h3>
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <Loader message="Loading subscription catalog..." className="min-h-[300px]" />
      ) : plans.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent className="flex flex-col items-center justify-center gap-4">
            <DocumentText size={48} className="text-muted-foreground" />
            <h3 className="text-lg font-semibold">No Subscription Plans Found</h3>
            <p className="text-sm text-muted-foreground max-w-sm">
              Please seed subscription plans or click the "Create Plan" button above to add a new tier.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Card Layout for Plans */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => {
              const enrollmentCount = getEnrollmentCount(plan.planCode);
              return (
                <Card 
                  key={plan._id} 
                  className={`border-t-4 shadow-sm relative overflow-hidden flex flex-col justify-between ${
                    // Dynamic top border styling based on hash of planCode
                    (() => {
                      const colors = [
                        'border-t-purple-600 dark:border-t-purple-500',
                        'border-t-blue-600 dark:border-t-blue-500',
                        'border-t-emerald-600 dark:border-t-emerald-500',
                        'border-t-rose-600 dark:border-t-rose-500',
                        'border-t-amber-600 dark:border-t-amber-500'
                      ];
                      if (plan.planCode === 'enterprise') return colors[0];
                      if (plan.planCode === 'midlevel') return colors[1];
                      if (plan.planCode === 'startup') return colors[2];
                      
                      const hash = String(plan.planCode).split('').reduce((a, b) => {
                        a = ((a << 5) - a) + b.charCodeAt(0);
                        return a & a;
                      }, 0);
                      return colors[Math.abs(hash) % colors.length];
                    })()
                  }`}
                >
                  <CardHeader className="pb-4">
                    <div className="flex justify-between items-start">
                      <Badge variant={plan.isActive ? "default" : "secondary"}>
                        {plan.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      {plan.isCustom && (
                        <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 dark:bg-amber-950/20">
                          Custom
                        </Badge>
                      )}
                    </div>
                    <CardTitle className="text-xl mt-2">{plan.planName}</CardTitle>
                    <CardDescription className="line-clamp-2 h-10 mt-1">{plan.description}</CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-4 flex-grow">
                    <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-xl text-center">
                      <span className="text-3xl font-extrabold text-foreground">
                        ₹{plan.price?.monthly || 0}
                      </span>
                      <span className="text-sm text-muted-foreground"> / month</span>
                      <div className="text-xs text-muted-foreground mt-1">
                        or ₹{plan.price?.annual || 0} billed annually
                      </div>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">Limits & Quotas</div>
                      <div className="flex justify-between border-b pb-1">
                        <span className="text-muted-foreground">Max Caregivers</span>
                        <span className="font-medium">
                          {plan.limits?.maxCaregivers === -1 ? 'Unlimited' : plan.limits?.maxCaregivers}
                        </span>
                      </div>
                      <div className="flex justify-between border-b pb-1">
                        <span className="text-muted-foreground">Max Patients</span>
                        <span className="font-medium">
                          {plan.limits?.maxPatients === -1 ? 'Unlimited' : plan.limits?.maxPatients}
                        </span>
                      </div>
                      <div className="flex justify-between border-b pb-1">
                        <span className="text-muted-foreground">Max Admins</span>
                        <span className="font-medium">
                          {plan.limits?.maxAdmins === -1 ? 'Unlimited' : plan.limits?.maxAdmins}
                        </span>
                      </div>
                      <div className="flex justify-between border-b pb-1">
                        <span className="text-muted-foreground">Max Upload Size</span>
                        <span className="font-medium">{plan.limits?.maxFileSizeMB || 5} MB</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total Storage</span>
                        <span className="font-medium">
                          {plan.limits?.totalStorageGB === -1 ? 'Unlimited' : `${plan.limits?.totalStorageGB} GB`}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">Enrolled Agencies</div>
                      <div className="flex items-center gap-1.5">
                        <Profile2User size={16} className="text-muted-foreground" />
                        <span className="font-bold text-foreground">{enrollmentCount}</span>
                        <span className="text-muted-foreground text-xs">active organization(s)</span>
                      </div>
                    </div>
                  </CardContent>

                  <div className="p-6 pt-4 border-t mt-4 flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => navigate(`/subscriptions/plans/edit/${plan._id}`)}
                      className="flex-grow flex items-center justify-center gap-1.5"
                      disabled={plan.isCustom === false}
                    >
                      <Edit size={16} />
                      {plan.isCustom === false ? 'Locked' : 'Edit Tier'}
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => navigate(`/subscriptions/assignments?plan=${plan.planCode}`)}
                      className="flex-grow flex items-center justify-center gap-1.5"
                    >
                      View Subscribers
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Details Table */}
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Plans Comparison Table</CardTitle>
              <CardDescription>Comprehensive grid detailing each plan configuration</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Plan Name</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead className="text-right">Monthly</TableHead>
                    <TableHead className="text-right">Annual</TableHead>
                    <TableHead className="text-center">Caregivers</TableHead>
                    <TableHead className="text-center">Patients</TableHead>
                    <TableHead className="text-center">Billing</TableHead>
                    <TableHead className="text-center">SMS</TableHead>
                    <TableHead className="text-center">AI Schedule</TableHead>
                    <TableHead className="text-center">Subscribers</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plans.map((plan) => (
                    <TableRow key={plan._id}>
                      <TableCell className="font-semibold">{plan.planName}</TableCell>
                      <TableCell><code className="text-xs bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded">{plan.planCode}</code></TableCell>
                      <TableCell className="text-right font-medium">₹{plan.price?.monthly}</TableCell>
                      <TableCell className="text-right font-medium">₹{plan.price?.annual}</TableCell>
                      <TableCell className="text-center font-medium">
                        {plan.limits?.maxCaregivers === -1 ? '∞' : plan.limits?.maxCaregivers}
                      </TableCell>
                      <TableCell className="text-center font-medium">
                        {plan.limits?.maxPatients === -1 ? '∞' : plan.limits?.maxPatients}
                      </TableCell>
                      <TableCell className="text-center">
                        {plan.features?.billingModule ? (
                          <TickCircle size={20} className="text-emerald-500 mx-auto" />
                        ) : (
                          <CloseCircle size={20} className="text-rose-500 mx-auto" />
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {plan.features?.smsNotifications ? (
                          <TickCircle size={20} className="text-emerald-500 mx-auto" />
                        ) : (
                          <CloseCircle size={20} className="text-rose-500 mx-auto" />
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {plan.features?.aiScheduling ? (
                          <TickCircle size={20} className="text-emerald-500 mx-auto" />
                        ) : (
                          <CloseCircle size={20} className="text-rose-500 mx-auto" />
                        )}
                      </TableCell>
                      <TableCell className="text-center font-bold">
                        {getEnrollmentCount(plan.planCode)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => navigate(`/subscriptions/plans/edit/${plan._id}`)}
                          className="text-primary hover:text-primary-foreground hover:bg-primary"
                          disabled={plan.isCustom === false}
                        >
                          {plan.isCustom === false ? 'Locked' : 'Edit'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default PlanList;
